import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@nucleus/ui-base';
import { Runner, TaskDefinition } from '../../../api/runner-service';
import { RunnerControllerService } from '../../../api/runner-service/api/runnerController.service';

/**
 * Detail / drill-down view of a single registered runner. Read-only — same
 * shape as the list page row, plus the JSON-formatted capabilities /
 * connectors / tenants_allowed payloads in full.
 */
@Component({
    selector: 'app-runner',
    templateUrl: './runner.component.html',
    styleUrl: './runner.component.css',
    standalone: false,
})
export class RunnerAdminComponent extends BaseComponent implements OnInit {

    private static readonly STALE_AFTER_MS = 90_000;

    private runnerControllerService: RunnerControllerService = inject(RunnerControllerService);
    private route: ActivatedRoute = inject(ActivatedRoute);

    runner       = signal<Runner | null>(null);
    tasksOffered = signal<TaskDefinition[]>([]);
    /** Per-task expand/collapse state for the input-schema accordion. Keyed by
     *  `${taskType}:${definitionVersion}` so two versions of the same task can
     *  be opened independently. */
    expanded     = signal<Record<string, boolean>>({});
    loading      = signal<boolean>(true);

    ngOnInit(): void {
        if (!this.permissionChecker.hasAccessToViewRunnersAdmin()) {
            this.showAccessDeny();
            return;
        }
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            this.showError('No runner id supplied.');
            this.navigateTo('/administration/runners', null);
            return;
        }
        this.runnerControllerService.findById(id).subscribe({
            next: (r) => { this.runner.set(r); this.loading.set(false); },
            error: (err) => { this.loading.set(false); this.showError(err, 'Failed to load runner'); },
        });
        // Tasks-offered fetch is independent — failure here doesn't block the
        // page from rendering the registry attributes.
        this.runnerControllerService.findTasksOfferedByRunner(id).subscribe({
            next: (list) => this.tasksOffered.set(list ?? []),
            error: () => this.tasksOffered.set([]),
        });
    }

    taskKey(t: TaskDefinition): string {
        return `${t.taskType}:${t.definitionVersion}`;
    }

    isExpanded(t: TaskDefinition): boolean {
        return !!this.expanded()[this.taskKey(t)];
    }

    toggleExpand(t: TaskDefinition): void {
        const key = this.taskKey(t);
        this.expanded.update(prev => ({ ...prev, [key]: !prev[key] }));
    }

    /** Friendly summary of an input-schema's required fields — used as the
     *  collapsed-row hint so operators can scan task contracts at a glance
     *  without opening every panel. */
    requiredFieldHint(schema: any): string {
        if (!schema || typeof schema !== 'object') return 'no input';
        const required: string[] = Array.isArray(schema.required) ? schema.required : [];
        const props = schema.properties && typeof schema.properties === 'object'
                ? Object.keys(schema.properties) : [];
        if (required.length === 0 && props.length === 0) return 'no input';
        if (required.length === 0) return `${props.length} optional field${props.length === 1 ? '' : 's'}`;
        return `requires: ${required.join(', ')}`;
    }

    isStale(): boolean {
        const r = this.runner();
        if (!r?.lastSeen) return true;
        return new Date(r.lastSeen).getTime() < Date.now() - RunnerAdminComponent.STALE_AFTER_MS;
    }

    formatList(value: any): string {
        if (!value) return '';
        if (Array.isArray(value)) return value.join(', ');
        return String(value);
    }

    /** Pretty-print JSON for the raw-payload panels. Falls back to the empty
     *  string when there's nothing to render so the panel collapses. */
    formatJson(value: any): string {
        if (value === null || value === undefined) return '';
        try { return JSON.stringify(value, null, 2); } catch { return String(value); }
    }

    onBack(): void { this.navigateTo('/administration/runners', null); }
}
